/** Maintained finite scrolling shooter mission. World units and integer ticks
 * are authoritative; renderers, clocks, input devices and providers stay out.
 * Projectiles use the shared lifecycle/swept-contact kernel. */
import {
  advanceProjectiles,
  createProjectilePilot,
  fireProjectile,
  resolveSweptProjectile,
  type ProjectilePilotState,
} from './projectileLifecycle.js';
import { overlapShape2d } from './collision2d.js';

export type ShooterMissionVariantV1 = 'standard' | 'alternate';
export type ShooterMissionCommandV1 = {
  type: 'pilot'; dx: -1 | 0 | 1; dy: -1 | 0 | 1; cannon: boolean; bomb: boolean;
};
export interface ShooterMissionEnemyConfigV1 {
  id: string; x: number; y: number; w: number; h: number; hp: number; score: number; fireTicks: number[];
}
export interface ShooterMissionWaveConfigV1 { id: string; startTick: number; enemies: ShooterMissionEnemyConfigV1[] }
export interface ShooterMissionWeaponConfigV1 {
  speed: number; lifetimeTicks: number; cooldownTicks: number; maxLive: number; damage: number;
}
export interface ShooterMissionConfigV1 {
  profile: 'shooter-mission@1';
  variant: ShooterMissionVariantV1;
  world: { width: number; height: number; scrollSpeed: number; playerMinOffset: number; playerMaxOffset: number };
  player: { startY: number; speed: number; w: number; h: number; lives: number; invulnerabilityTicks: number; hitFlashTicks: number };
  weapons: {
    cannon: ShooterMissionWeaponConfigV1;
    bomb: ShooterMissionWeaponConfigV1 & { blastRadius: number };
    enemy: ShooterMissionWeaponConfigV1;
  };
  rules: { timeLimitTicks: number; completionDistance: number };
  waves: ShooterMissionWaveConfigV1[];
}
export interface ShooterMissionEnemyV1 {
  id: string; waveId: string; x: number; y: number; w: number; h: number; hp: number; maxHp: number; score: number;
}
export interface ShooterMissionProjectileV1 {
  id: string; kind: 'cannon' | 'bomb' | 'enemy-shot'; owner: string; x: number; y: number; vx: number; vy: number;
}
export interface ShooterMissionSnapshotV1 {
  tick: number; variant: ShooterMissionVariantV1; score: number;
  phase: 'active' | 'won' | 'lost'; lossCause: null | 'lives' | 'time';
  distance: number; timeLeft: number;
  player: { x: number; y: number; vx: number; vy: number; w: number; h: number; lives: number; invulnerable: boolean; hitStunned: boolean };
  waves: Array<{ id: string; status: 'pending' | 'active' | 'cleared'; remaining: number }>;
  enemies: ShooterMissionEnemyV1[];
  projectiles: ShooterMissionProjectileV1[];
  defeatedIds: string[]; totalEnemies: number; shotsFired: number; bombsFired: number; hitsTaken: number;
}
export type ShooterMissionEventV1 =
  | { kind: 'cannon' | 'bomb'; tick: number; data: { projectileId: string; x: number; y: number } }
  | { kind: 'enemy-fire'; tick: number; data: { projectileId: string; enemyId: string; x: number; y: number } }
  | { kind: 'enemy-hit'; tick: number; data: { enemyId: string; projectileId: string; source: 'cannon' | 'bomb'; x: number; y: number; hp: number } }
  | { kind: 'enemy-destroyed'; tick: number; data: { enemyId: string; source: 'cannon' | 'bomb'; x: number; y: number; score: number } }
  | { kind: 'hurt'; tick: number; data: { x: number; y: number; lives: number } }
  | { kind: 'wave'; tick: number; data: { waveId: string; status: 'active' | 'cleared' } }
  | { kind: 'won'; tick: number; data: { score: number } }
  | { kind: 'lost'; tick: number; data: { cause: 'lives' | 'time'; score: number } };

const base = {
  profile: 'shooter-mission@1' as const,
  world: { width: 44, height: 12, scrollSpeed: 3, playerMinOffset: 1.5, playerMaxOffset: 5 },
  player: { startY: 6, speed: 7.5, w: 0.9, h: 0.8, lives: 3, invulnerabilityTicks: 30, hitFlashTicks: 8 },
  weapons: {
    cannon: { speed: 18, lifetimeTicks: 75, cooldownTicks: 4, maxLive: 8, damage: 1 },
    bomb: { speed: 11, lifetimeTicks: 105, cooldownTicks: 24, maxLive: 2, damage: 2, blastRadius: 2.25 },
    enemy: { speed: 9, lifetimeTicks: 120, cooldownTicks: 1, maxLive: 8, damage: 1 },
  },
  rules: { timeLimitTicks: 520, completionDistance: 36 },
};

const standardWaves: ShooterMissionWaveConfigV1[] = [
  { id: 'wave-1', startTick: 1, enemies: [
    { id: 'scout-a', x: 10, y: 6, w: 1, h: 1, hp: 1, score: 100, fireTicks: [30, 75, 120, 165] },
    { id: 'scout-b', x: 12.2, y: 3.2, w: 1, h: 1, hp: 1, score: 100, fireTicks: [35, 95, 155] },
  ] },
  { id: 'wave-2', startTick: 80, enemies: [
    { id: 'wing-a', x: 19.2, y: 4.2, w: 1.1, h: 1.1, hp: 1, score: 125, fireTicks: [35, 85, 135] },
    { id: 'wing-b', x: 20.8, y: 7.8, w: 1.1, h: 1.1, hp: 1, score: 125, fireTicks: [40, 90, 140] },
  ] },
  { id: 'wave-3', startTick: 160, enemies: [
    { id: 'core-a', x: 28, y: 3.2, w: 1.2, h: 1.2, hp: 2, score: 175, fireTicks: [40, 85, 130, 175] },
    { id: 'core-b', x: 28.8, y: 6, w: 1.2, h: 1.2, hp: 2, score: 175, fireTicks: [45, 90, 135, 180] },
    { id: 'core-c', x: 29.6, y: 8.8, w: 1.2, h: 1.2, hp: 2, score: 175, fireTicks: [50, 95, 140, 185] },
  ] },
];

const alternateWaves: ShooterMissionWaveConfigV1[] = [
  { id: 'wave-1', startTick: 1, enemies: [
    { id: 'dart-a', x: 9.6, y: 5.5, w: 0.9, h: 0.9, hp: 1, score: 90, fireTicks: [30, 70, 110, 150] },
    { id: 'dart-b', x: 11.5, y: 8.5, w: 0.9, h: 0.9, hp: 1, score: 90, fireTicks: [35, 75, 115, 155] },
  ] },
  { id: 'wave-2', startTick: 72, enemies: [
    { id: 'guard-a', x: 18.4, y: 2.8, w: 1.2, h: 1.2, hp: 2, score: 140, fireTicks: [40, 85, 130, 175] },
    { id: 'guard-b', x: 19.5, y: 5.5, w: 1.2, h: 1.2, hp: 2, score: 140, fireTicks: [45, 90, 135, 180] },
    { id: 'guard-c', x: 20.6, y: 8.2, w: 1.2, h: 1.2, hp: 2, score: 140, fireTicks: [50, 95, 140, 185] },
  ] },
  { id: 'wave-3', startTick: 155, enemies: [
    { id: 'fort-a', x: 28.2, y: 4, w: 1.4, h: 1.4, hp: 2, score: 200, fireTicks: [40, 80, 120, 160, 200] },
    { id: 'fort-b', x: 29.2, y: 7, w: 1.4, h: 1.4, hp: 2, score: 200, fireTicks: [45, 85, 125, 165, 205] },
  ] },
];

/** Returns one of exactly two maintained data variants, always detached. */
export function shooterMissionConfigV1(variant: ShooterMissionVariantV1 = 'standard'): ShooterMissionConfigV1 {
  if (variant !== 'standard' && variant !== 'alternate') throw new RangeError('Unsupported shooter mission variant');
  return structuredClone({ ...base, variant, waves: variant === 'standard' ? standardWaves : alternateWaves });
}

function object(value: unknown, keys: readonly string[]): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(value))
    || Reflect.ownKeys(value).length !== keys.length
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')
    || Object.values(Object.getOwnPropertyDescriptors(value)).some(d => !('value' in d))) {
    throw new RangeError('Invalid shooter mission record');
  }
}
function finite(value: unknown, min: number, max: number, integer = false): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max
    || integer && !Number.isSafeInteger(value)) throw new RangeError('Invalid shooter mission numeric bound');
}
function id(value: unknown, ids: Set<string>): asserts value is string {
  if (typeof value !== 'string' || !/^[a-z][a-z0-9-]{0,39}$/.test(value) || ids.has(value)) throw new RangeError('Invalid shooter mission entity ID');
  ids.add(value);
}

export function validateShooterMissionConfigV1(input: unknown): ShooterMissionConfigV1 {
  object(input, ['profile', 'variant', 'world', 'player', 'weapons', 'rules', 'waves']);
  if (input.profile !== 'shooter-mission@1' || !['standard','alternate'].includes(String(input.variant))) throw new RangeError('Unsupported shooter mission profile');
  object(input.world, ['width','height','scrollSpeed','playerMinOffset','playerMaxOffset']);
  finite(input.world.width, 20, 100); finite(input.world.height, 8, 30); finite(input.world.scrollSpeed, 0.5, 12);
  finite(input.world.playerMinOffset, 0.5, 10); finite(input.world.playerMaxOffset, Number(input.world.playerMinOffset), 12);
  object(input.player, ['startY','speed','w','h','lives','invulnerabilityTicks','hitFlashTicks']);
  finite(input.player.startY, 0.5, Number(input.world.height) - 0.5); finite(input.player.speed, 1, 30);
  finite(input.player.w, 0.2, 3); finite(input.player.h, 0.2, 3); finite(input.player.lives, 1, 9, true);
  finite(input.player.invulnerabilityTicks, 0, 300, true); finite(input.player.hitFlashTicks, 0, Number(input.player.invulnerabilityTicks), true);
  object(input.weapons, ['cannon','bomb','enemy']);
  for (const kind of ['cannon','bomb','enemy'] as const) {
    const keys = kind === 'bomb' ? ['speed','lifetimeTicks','cooldownTicks','maxLive','damage','blastRadius'] : ['speed','lifetimeTicks','cooldownTicks','maxLive','damage'];
    object(input.weapons[kind], keys);
    const weapon = input.weapons[kind];
    finite(weapon.speed, 1, 60); finite(weapon.lifetimeTicks, 1, 600, true); finite(weapon.cooldownTicks, 0, 300, true);
    finite(weapon.maxLive, 1, 32, true); finite(weapon.damage, 1, 10, true);
    if (kind === 'bomb') finite(weapon.blastRadius, 0.5, 8);
  }
  object(input.rules, ['timeLimitTicks','completionDistance']);
  finite(input.rules.timeLimitTicks, 60, 3600, true);
  finite(input.rules.completionDistance, Number(input.world.playerMaxOffset), Number(input.world.width) - Number(input.world.playerMaxOffset));
  if (Number(input.rules.completionDistance) / Number(input.world.scrollSpeed) * 30 >= Number(input.rules.timeLimitTicks)) throw new RangeError('Shooter mission cannot finish before its clock');
  if (!Array.isArray(input.waves) || input.waves.length < 1 || input.waves.length > 8) throw new RangeError('Invalid shooter mission wave count');
  const ids = new Set<string>(); let enemies = 0, previousStart = -1;
  for (const wave of input.waves) {
    object(wave, ['id','startTick','enemies']); id(wave.id, ids);
    finite(wave.startTick, 1, Number(input.rules.timeLimitTicks) - 1, true);
    if (wave.startTick <= previousStart) throw new RangeError('Shooter mission waves must be ordered'); previousStart = wave.startTick;
    if (!Array.isArray(wave.enemies) || wave.enemies.length < 1 || wave.enemies.length > 8) throw new RangeError('Invalid shooter mission enemy count');
    enemies += wave.enemies.length;
    for (const enemy of wave.enemies) {
      object(enemy, ['id','x','y','w','h','hp','score','fireTicks']); id(enemy.id, ids);
      finite(enemy.x, Number(input.world.playerMaxOffset) + 1, Number(input.rules.completionDistance) + Number(input.world.playerMaxOffset));
      finite(enemy.y, 0.5, Number(input.world.height) - 0.5); finite(enemy.w, 0.2, 4); finite(enemy.h, 0.2, 4);
      finite(enemy.hp, 1, 20, true); finite(enemy.score, 1, 10_000, true);
      const latestPlayerX = Number(input.world.scrollSpeed) * Number(wave.startTick) / 30 + Number(input.world.playerMaxOffset);
      if (latestPlayerX + enemy.w / 2 >= enemy.x) throw new RangeError('Shooter mission wave activates behind the player');
      if (!Array.isArray(enemy.fireTicks) || enemy.fireTicks.length > 16) throw new RangeError('Invalid enemy fire schedule');
      let prior = -1;
      for (const at of enemy.fireTicks) {
        finite(at, 0, Number(input.rules.timeLimitTicks) - Number(wave.startTick), true);
        if (at <= prior) throw new RangeError('Enemy fire ticks must be unique and ordered'); prior = at;
      }
    }
  }
  if (enemies > 32) throw new RangeError('Shooter mission entity budget exceeded');
  const config = structuredClone(input) as unknown as ShooterMissionConfigV1;
  for (const wave of config.waves) wave.enemies.sort((a,b) => a.id.localeCompare(b.id, 'en'));
  return config;
}

interface EnemyState extends ShooterMissionEnemyV1 { contactId: number; startTick: number; fireTicks: readonly number[] }
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

export function createShooterMissionV1(input: ShooterMissionConfigV1) {
  const config = validateShooterMissionConfigV1(input);
  const all = config.waves.flatMap((wave, waveIndex) => wave.enemies.map((enemy, enemyIndex) => ({
    ...enemy, waveId: wave.id, maxHp: enemy.hp, contactId: 100 + waveIndex * 8 + enemyIndex,
    startTick: wave.startTick, fireTicks: enemy.fireTicks,
  } satisfies EnemyState)));
  const enemyByContact = new Map(all.map(enemy => [enemy.contactId, enemy]));
  let enemies = all.map(enemy => ({ ...enemy, fireTicks: [...enemy.fireTicks] }));
  let tick = 0, distance = 0, offset = config.world.playerMinOffset + 0.75;
  let playerY = config.player.startY, playerVx = config.world.scrollSpeed, playerVy = 0;
  let lives = config.player.lives, grace = 0, score = 0, shotsFired = 0, bombsFired = 0, hitsTaken = 0;
  let phase: ShooterMissionSnapshotV1['phase'] = 'active';
  let lossCause: ShooterMissionSnapshotV1['lossCause'] = null;
  let pending: ShooterMissionCommandV1 | null = null;
  let nextProjectileId = 1;
  let events: ShooterMissionEventV1[] = [];
  const activated = new Set<string>(), cleared = new Set<string>(), defeated = new Set<string>();
  const pilot = (weapon: ShooterMissionWeaponConfigV1, mask: number) => createProjectilePilot({
    spawnOffset: { x: 0, y: 0 }, speed: weapon.speed, lifetimeTicks: weapon.lifetimeTicks,
    cooldownTicks: weapon.cooldownTicks, maxLivePerOwner: weapon.maxLive, collisionMask: mask,
  });
  let cannonState = pilot(config.weapons.cannon, 1 << 1);
  let bombState = pilot(config.weapons.bomb, 1 << 1);
  let enemyShotState = pilot(config.weapons.enemy, 1 << 2);
  const playerX = () => distance + offset;
  const liveEnemies = () => enemies.filter(enemy => enemy.hp > 0 && tick >= enemy.startTick);
  const lose = (cause: 'lives' | 'time') => {
    phase = 'lost'; lossCause = cause; events.push({ kind: 'lost', tick, data: { cause, score } });
  };
  const hurt = () => {
    if (phase !== 'active' || grace > 0) return;
    lives--; hitsTaken++; grace = config.player.invulnerabilityTicks;
    events.push({ kind: 'hurt', tick, data: { x: playerX(), y: playerY, lives } });
    if (lives <= 0) lose('lives');
  };
  const damageEnemy = (contactId: number, damage: number, source: 'cannon'|'bomb', projectileId: string, x: number, y: number) => {
    const enemy = enemyByContact.get(contactId);
    if (!enemy) return;
    const current = enemies.find(value => value.id === enemy.id);
    if (!current || current.hp <= 0) return;
    current.hp = Math.max(0, current.hp - damage);
    events.push({ kind: 'enemy-hit', tick, data: { enemyId: current.id, projectileId, source, x, y, hp: current.hp } });
    if (current.hp === 0) {
      defeated.add(current.id); score += current.score;
      events.push({ kind: 'enemy-destroyed', tick, data: { enemyId: current.id, source, x: current.x, y: current.y, score: current.score } });
    }
  };
  const spawn = (state: ProjectilePilotState, weapon: ShooterMissionWeaponConfigV1, owner: number,
    x: number, y: number, direction: 1|-1, kind: 'cannon'|'bomb'|'enemy-shot', ownerId: string) => {
    const id = nextProjectileId;
    const fired = fireProjectile(state, { id, owner, atTick: tick, x, y, directionX: direction, directionY: 0 });
    if (!fired.accepted) return { state, accepted: false as const };
    nextProjectileId++;
    const projectileId = `${kind}-${id}`;
    if (kind === 'enemy-shot') events.push({ kind: 'enemy-fire', tick, data: { projectileId, enemyId: ownerId, x, y } });
    else events.push({ kind, tick, data: { projectileId, x, y } });
    return { state: fired.state, accepted: true as const, projectileId };
  };
  const advance = (state: ProjectilePilotState) => advanceProjectiles(state, tick, 1/30,
    projectile => projectile.x >= -8 && projectile.x <= config.world.width + 8
      && projectile.y >= -4 && projectile.y <= config.world.height + 4).state;
  const targets = () => liveEnemies().map(enemy => ({ id: enemy.contactId, layer: 1, kind: 'damage' as const,
    from: { x: enemy.x, y: enemy.y }, to: { x: enemy.x, y: enemy.y }, w: enemy.w, h: enemy.h }));
  const resolvePlayerShots = (kind: 'cannon'|'bomb') => {
    let state = kind === 'cannon' ? cannonState : bombState;
    for (const projectile of [...state.projectiles].sort((a,b) => a.id - b.id)) {
      const result = resolveSweptProjectile(state, projectile.id, { atTick: tick, dtSeconds: 1/30, w: 0.34,
        h: kind === 'bomb' ? 0.6 : 0.24, targets: targets() });
      state = result.state;
      const contact = result.contacts.find(value => value.outcome === 'hit');
      if (!contact) continue;
      const projectileId = `${kind}-${projectile.id}`;
      if (kind === 'cannon') damageEnemy(contact.targetId, config.weapons.cannon.damage, kind, projectileId, contact.x, contact.y);
      else {
        const victims = liveEnemies().filter(enemy => Math.hypot(enemy.x - contact.x, enemy.y - contact.y) <= config.weapons.bomb.blastRadius)
          .sort((a,b) => a.id.localeCompare(b.id, 'en'));
        for (const victim of victims) damageEnemy(victim.contactId, config.weapons.bomb.damage, kind, projectileId, contact.x, contact.y);
      }
    }
    if (kind === 'cannon') cannonState = state; else bombState = state;
  };
  return {
    applyCommand(slot: number, command: unknown): boolean {
      if (slot !== 0 || phase !== 'active') return false;
      try { object(command, ['type','dx','dy','cannon','bomb']); } catch { return false; }
      if (command.type !== 'pilot' || ![-1,0,1].includes(command.dx as number) || ![-1,0,1].includes(command.dy as number)
        || typeof command.cannon !== 'boolean' || typeof command.bomb !== 'boolean' || command.cannon && command.bomb) return false;
      pending = command as ShooterMissionCommandV1; return true;
    },
    step(): void {
      if (phase !== 'active') return;
      tick++;
      const command = pending ?? { type: 'pilot', dx: 0, dy: 0, cannon: false, bomb: false };
      pending = null;
      const previousPlayer = { x: playerX(), y: playerY };
      distance = Math.min(config.rules.completionDistance, distance + config.world.scrollSpeed / 30);
      offset = clamp(offset + command.dx * config.player.speed / 30, config.world.playerMinOffset, config.world.playerMaxOffset);
      playerY = clamp(playerY + command.dy * config.player.speed / 30, config.player.h / 2, config.world.height - config.player.h / 2);
      playerVx = config.world.scrollSpeed + command.dx * config.player.speed;
      playerVy = command.dy * config.player.speed;
      for (const wave of config.waves) if (tick >= wave.startTick && !activated.has(wave.id)) {
        activated.add(wave.id); events.push({ kind: 'wave', tick, data: { waveId: wave.id, status: 'active' } });
      }
      if (command.cannon) {
        const result = spawn(cannonState, config.weapons.cannon, 0, playerX() + config.player.w / 2, playerY, 1, 'cannon', 'player');
        cannonState = result.state; if (result.accepted) shotsFired++;
      } else if (command.bomb) {
        const result = spawn(bombState, config.weapons.bomb, 0, playerX() + config.player.w / 2, playerY, 1, 'bomb', 'player');
        bombState = result.state; if (result.accepted) bombsFired++;
      }
      for (const enemy of liveEnemies().sort((a,b) => a.id.localeCompare(b.id, 'en'))) {
        if (!enemy.fireTicks.includes(tick - enemy.startTick)) continue;
        const result = spawn(enemyShotState, config.weapons.enemy, enemy.contactId, enemy.x - enemy.w / 2,
          enemy.y, -1, 'enemy-shot', enemy.id);
        enemyShotState = result.state;
      }
      cannonState = advance(cannonState); bombState = advance(bombState); enemyShotState = advance(enemyShotState);
      resolvePlayerShots('cannon'); resolvePlayerShots('bomb');
      for (const projectile of [...enemyShotState.projectiles].sort((a,b) => a.id - b.id)) {
        const result = resolveSweptProjectile(enemyShotState, projectile.id, { atTick: tick, dtSeconds: 1/30, w: 0.28, h: 0.28,
          targets: [{ id: 1, layer: 2, kind: 'damage', from: previousPlayer, to: { x: playerX(), y: playerY }, w: config.player.w, h: config.player.h }] });
        enemyShotState = result.state;
        if (result.contacts.some(value => value.outcome === 'hit')) hurt();
      }
      if (grace > 0) grace--;
      if (phase === 'active' && liveEnemies().some(enemy => overlapShape2d(
        { kind: 'aabb', x: playerX(), y: playerY, w: config.player.w, h: config.player.h },
        { kind: 'aabb', x: enemy.x, y: enemy.y, w: enemy.w, h: enemy.h },
      ))) hurt();
      for (const wave of config.waves) if (activated.has(wave.id) && !cleared.has(wave.id)
        && enemies.filter(enemy => enemy.waveId === wave.id).every(enemy => enemy.hp === 0)) {
        cleared.add(wave.id); events.push({ kind: 'wave', tick, data: { waveId: wave.id, status: 'cleared' } });
      }
      if (phase !== 'active') return;
      if (defeated.size === enemies.length && distance >= config.rules.completionDistance) {
        phase = 'won'; events.push({ kind: 'won', tick, data: { score } });
      } else if (tick >= config.rules.timeLimitTicks) lose('time');
    },
    snapshot(): ShooterMissionSnapshotV1 {
      const projectile = (state: ProjectilePilotState, kind: ShooterMissionProjectileV1['kind']): ShooterMissionProjectileV1[] =>
        state.projectiles.map(value => ({ id: `${kind}-${value.id}`, kind, owner: value.owner === 0 ? 'player'
          : enemies.find(enemy => enemy.contactId === value.owner)?.id ?? `enemy-${value.owner}`,
        x: value.x, y: value.y, vx: value.vx, vy: value.vy }));
      return structuredClone({
        tick, variant: config.variant, score, phase, lossCause, distance,
        timeLeft: Math.max(0, config.rules.timeLimitTicks - tick),
        player: { x: playerX(), y: playerY, vx: playerVx, vy: playerVy, w: config.player.w, h: config.player.h,
          lives, invulnerable: grace > 0, hitStunned: grace > config.player.invulnerabilityTicks - config.player.hitFlashTicks },
        waves: config.waves.map(wave => ({ id: wave.id, status: cleared.has(wave.id) ? 'cleared' as const
          : activated.has(wave.id) ? 'active' as const : 'pending' as const,
        remaining: enemies.filter(enemy => enemy.waveId === wave.id && enemy.hp > 0).length })),
        enemies: liveEnemies().map(({ contactId: _contactId, startTick: _startTick, fireTicks: _fireTicks, ...enemy }) => enemy),
        projectiles: [...projectile(cannonState,'cannon'), ...projectile(bombState,'bomb'), ...projectile(enemyShotState,'enemy-shot')]
          .sort((a,b) => a.id.localeCompare(b.id, 'en')),
        defeatedIds: [...defeated].sort(), totalEnemies: enemies.length, shotsFired, bombsFired, hitsTaken,
      });
    },
    drainEvents(): ShooterMissionEventV1[] { const out = events; events = []; return out; },
  };
}
